import { useMutation } from '@apollo/client';
import {
    faArrowRotateRight,
    faPencil,
    faPlugCircleCheck,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import OrdatConnectionTestModal from './OrdatConnectionTestModal.tsx';
import {
    DELETE_ORDAT_INTEGRATION,
    GET_ORDAT_INTEGRATIONS,
    TEST_ORDAT_INTEGRATION_CONNECTION,
    TRIGGER_ORDAT_INTEGRATION_MASTER_DATA_UPDATE,
} from './queries.ts';
import type { OrdatConnectionTestResult, OrdatIntegration } from './types.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_ORDAT_EDIT_PATH } from '../../constants.ts';

const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const LoadingIcon = withIcon(faSpinnerThird);
const TestConnectionIcon = withIcon(faPlugCircleCheck);

const ChannelTags = ({ channels, label }: { channels: { id: string; name: string }[]; label: string }) => {
    if (channels.length === 0) return null;
    return (
        <div className="flex gap-1.5 flex-wrap items-center">
            <span className="text-content-medium text-xs">{label}:</span>
            {channels.map((channel) => (
                <span
                    key={channel.id}
                    className="border-line-weak border border-solid bg-surface-strong px-1 rounded text-xs break-words"
                >
                    {channel.name}
                </span>
            ))}
        </div>
    );
};

const MasterDataStatus = ({ integration }: { integration: OrdatIntegration }) => {
    const { t } = useTranslation('integrations');

    if (integration.masterDataUpdateActive) {
        return (
            <span className="flex gap-2 items-center animate-pulse">
                <span className="text-content-success-strong">
                    <LoadingIcon spin />
                </span>
                <span>
                    {`${t('ordat.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                </span>
            </span>
        );
    }

    if (integration.masterDataUpdateError) {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-content-danger-strong">{t('ordat.masterDataUpdateError')}</span>
                <span
                    className="text-content-medium text-xs whitespace-pre-line break-words max-w-xs line-clamp-4"
                    title={integration.masterDataUpdateError}
                >
                    {integration.masterDataUpdateError}
                </span>
            </div>
        );
    }

    if (!integration.masterDataUpdatedAt) {
        return <span className="text-content-medium">{t('ordat.neverUpdated')}</span>;
    }

    return (
        <span>
            {t('ordat.lastMasterDataUpdate', {
                updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
            })}
        </span>
    );
};

export const OrdatRow = ({ integration, refetch }: { integration: OrdatIntegration; refetch: () => Promise<any> }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
    const [connectionTestResult, setConnectionTestResult] = useState<OrdatConnectionTestResult | null>(null);

    const [deleteIntegration] = useMutation(DELETE_ORDAT_INTEGRATION, {
        refetchQueries: [GET_ORDAT_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        setIsDeleteConfirmVisible(false);
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('ordat.deleteSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('ordat.deleteError'), status: 'error' });
        }
    };

    const [updateMasterData] = useMutation(TRIGGER_ORDAT_INTEGRATION_MASTER_DATA_UPDATE);
    const handleUpdateMasterData = async () => {
        try {
            await updateMasterData({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('ordat.updateMasterDataSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('ordat.updateMasterDataError'), status: 'error' });
        }
    };

    const [testConnection, { loading: isTesting }] = useMutation(TEST_ORDAT_INTEGRATION_CONNECTION);
    const handleTestConnection = async () => {
        try {
            const response = await testConnection({ variables: { id: integration.id } });
            const result = response.data?.testOrdatIntegrationConnection;
            if (!result) throw new Error('empty testOrdatIntegrationConnection response');
            setConnectionTestResult(result);
            await refetch();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('ordat.connectionTest.requestError'), status: 'error' });
        }
    };

    return (
        <>
            <Table.Row>
                <Table.Cell className="align-top py-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-medium">{integration.name}</span>
                        <span className="text-content-medium text-xs break-all" title={integration.baseUrl}>
                            {integration.baseUrl}
                        </span>
                        {integration.companyName && (
                            <span className="text-content-medium text-xs">
                                {t('ordat.table.companyName')}: {integration.companyName}
                            </span>
                        )}
                    </div>
                </Table.Cell>
                <Table.Cell className="align-top py-3">
                    <div className="flex flex-col gap-1.5">
                        <ChannelTags channels={integration.orderChannels} label={t('ordat.table.orderChannels')} />
                        <ChannelTags channels={integration.rfqChannels} label={t('ordat.table.rfqChannels')} />
                    </div>
                </Table.Cell>
                <Table.Cell className="align-top py-3">
                    <MasterDataStatus integration={integration} />
                </Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <RowControlIconButton onClick={handleTestConnection} disabled={isTesting}>
                            {isTesting ? <LoadingIcon spin /> : <TestConnectionIcon />}
                        </RowControlIconButton>

                        <RowControlIconButton onClick={handleUpdateMasterData}>
                            <RefreshIcon />
                        </RowControlIconButton>

                        <RowControlIconButton asChild>
                            <Link
                                to={url(CUSTOMER_INTEGRATION_ORDAT_EDIT_PATH, { integrationId: integration.id })}
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <EditIcon />
                            </Link>
                        </RowControlIconButton>

                        <RowControlIconButton
                            className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                            onClick={() => setIsDeleteConfirmVisible(true)}
                        >
                            <DeleteIcon />
                        </RowControlIconButton>
                    </div>
                </Table.Cell>
            </Table.Row>

            {isDeleteConfirmVisible && (
                <ConfirmModal
                    danger
                    title={t('ordat.deleteConfirm.title')}
                    labelConfirm={t('ordat.deleteConfirm.confirmButton')}
                    onConfirm={handleDeleteIntegration}
                    onCancel={() => setIsDeleteConfirmVisible(false)}
                >
                    {t('ordat.deleteConfirm.description', { name: integration.name })}
                </ConfirmModal>
            )}

            {connectionTestResult && (
                <OrdatConnectionTestModal
                    integrationName={integration.name}
                    result={connectionTestResult}
                    onClose={() => setConnectionTestResult(null)}
                />
            )}
        </>
    );
};

export default OrdatRow;
